import React from "react";

const ConflictsOfInterest = () => {
  return (
    <React.Fragment>
      <h1>Competing interests</h1>
      <p>
        CJBE requires all parties involved in the publication process (authors, reviewers, academic
        editors, and post-publication SIQ™ scorers) to transparently and fully declare any potential
        COI.
      </p>
      <p>
        Authors are required to declare all competing interests during the submission process.
        Reviewers are expected to consider any potential COI before agreeing to review, and to
        declare, in detail, any relevant COI before submitting their review. Editors and reviewers
        are expected to recuse themselves from the publication process if they feel they have COI in
        regard to the article.
      </p>
      <p>
        CJBE Editorial Board members and associate editors are welcome to submit articles to CJBE.
        However, they may have absolutely no part in the handling of their manuscript at any stage
        of the publication process. While we recognize that eliminating all bias is impossible, we
        ensure that every effort is made to minimize any bias during the evaluation and processing
        of these submissions.
      </p>
      <p>
        All potential COI should be declared. Please disclose a relationship and/or COI, if faced
        with even the slightest amount of uncertainty as to its potential relevance. Examples of COI
        include, but are not limited to: possible financial benefits if the article is published,
        prior working or personal relationships with any of the authors, patent or grant activity
        that could be considered broadly relevant to the work, personal content or financial gain
        (such as free travel, gifts, etc.) relating to the work, personal convictions (religious,
        political, etc.) which may have a bearing on the work, etc.
      </p>
      <p>
        A complete list of funding sources (institutional, corporate, grants, etc.) and relevant
        relationships and financial activities must be detailed in the Ethics Statements and COI
        Disclosures section of the article submission process.
      </p>
      <p>
        The following COI sections contain questions posed to the authors during the submission
        process. All COI sections must be addressed in order to satisfy CJBE’s COI requirements and
        are reprinted below as they appear in the article submission process. The language contained
        in the four sections below is based upon the{" "}
        <a
          href="http://www.icmje.org/conflicts-of-interest"
          target="_blank"
          rel="noreferrer noopener"
        >
          ICMJE Form for Disclosure of Potential COI.
        </a>
        .
      </p>

      <h2>
        <strong>The Work Under Consideration for Publication</strong>
      </h2>
      <p>
        Did you or your institution at any time receive payment or services from a third party
        (government, commercial, private foundation, etc.) for any aspect of the submitted work
        (such as grants, data monitoring board, study design, manuscript preparation, statistical
        analysis, etc.)?
      </p>

      <h2>
        <strong>Relevant Financial Activities Outside the Submitted Work</strong>
      </h2>
      <p>
        Please indicate all financial relationships (regardless of compensation amount) associated
        with the submitting author and all co-authors. You should disclose interactions with any
        entity that could be considered broadly relevant to your work. Report all relationships that
        were present during the 36 months prior to publication. Remember: if there is any question,
        it is better to disclose a relationship than not to do so.
      </p>
      <p>
        For grants you have received for work outside the submitted work, only disclose support from
        entities that could be perceived to be affected financially by the published work (e.g.,
        foundations supported by entities that could be perceived to have a financial stake in the
        outcome).
      </p>

      <h2>
        <strong>Intellectual Property: Patents &amp; Copyrights</strong>
      </h2>
      <p>
        Do you have any patents (planned, pending, or issued) that could be considered broadly
        relevant to this work?
      </p>
      <p>
        If “Yes,” then please disclose it in the Disclosures &gt; Intellectual Property section of
        the manuscript.
      </p>
      <p>
        Also, please provide written permission, in English, from the copyright holder of the
        third-party material, to publish it under a CC BY open access license and ensure that they
        are acknowledged in the relevant figure legend. Information about this license can be found
        here:{" "}
        <a
          href="https://creativecommons.org/licenses/by/4.0/"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://creativecommons.org/licenses/by/4.0/
        </a>
        . Please ensure the permission document adheres to the following requirements:
      </p>

      <ul className="indented-for-medium-up disc">
        <li>
          Permission must be drafted in English, and it must explicitly state that the permission is
          granted to us (Springer Nature Limited), to publish the image under a CC BY open access
          license.
        </li>
        <li>
          Permission must be granted to publish the material in both formats (print and digital) and
          it must be signed by the copyright holder.
        </li>
      </ul>

      <p>
        Please send the above to our inbox at{" "}
        <a href="mailto:cureus-support@springernature.com">cureus-support@springernature.com</a>.
      </p>

      <p>
        Please note that our third-party material declaration is designed for authors to declare
        whether their manuscript contains material (e.g., figures, images, and/or supplementary
        material) from third parties, and whether appropriate permissions have been obtained if that
        material is protected by copyright. This declaration is not designed for authors to state
        where they have obtained physical materials from, and whether the supplier has had editorial
        input in the writing of the manuscript. If you would instead like to declare that all the
        material in your manuscript is owned by the authors and/or no permissions are required, then
        please change the declaration to “No.”
      </p>

      <p>
        For images modified from another publication/journal article, we recommend using
        Rightslink/Copyright Clearance Center to obtain copyright clearance{" "}
        <a
          href="https://marketplace.copyright.com/rs-ui-web/mp"
          target="_blank"
          rel="noopener noreferrer"
        >
          (https://marketplace.copyright.com/)
        </a>
        . The RightsLink link can also be found by clicking on the “Rights and Permissions” icon on
        the page of the online article. We ask for proof of copyright even for modified figures.
        Please ensure you select the following options from the drop-down menu on the license
        agreement and provide the full PDF version of the license:
      </p>

      <p>
        <strong>Type of use:</strong> Journal/Magazine
      </p>
      <p>
        <strong>Requestor type: </strong>STM/Publisher (STM signatory)/academic/university or
        research institute/author of new work
      </p>
      <p>
        <strong>Format:</strong> Electronic
      </p>
      <p>
        <strong>Publication the new article is in:</strong> Cureus Journals
      </p>
      <p>
        <strong>Publisher of new article:</strong> Cureus Journals
      </p>
      <p>
        Please send the permissions document to{" "}
        <a href="mailto:cureus-support@springernature.com">cureus-support@springernature.com</a>.
      </p>

      <h2>
        <strong>Relationships Not Covered Previously</strong>
      </h2>
      <p>
        Are there other relationships or activities that readers could perceive to have influenced,
        or that give the appearance of potentially influencing, what you wrote in the submitted
        work?
      </p>
      <p>
        <strong>
          If “Yes,” then please disclose it in the Disclosures &gt; Other Relationships section of
          the manuscript.
        </strong>
      </p>

      <h2>
        <strong>Disclosure of Third-Party Funding</strong>{" "}
      </h2>
      <p>
        Separately from declaring any COI, CJBE also requires that authors disclose any payments or
        services received from a third-party (government, commercial, private foundation, etc.) for
        any aspect of the submitted work (such as grants, data monitoring board, study design,
        article preparation, statistical analysis, etc.).
      </p>
      <p>
        This information is included in a disclosure statement attached to the final published
        article.
      </p>
      <p>Have you secured any third-party funding? </p>
      <p>
        <strong>
          If “Yes,” then please disclose it in the Disclosures &gt; Payments section of the
          manuscript.
        </strong>
      </p>
    </React.Fragment>
  );
};

export default ConflictsOfInterest;
